import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useAuth } from '../../contexts/AuthContext';
import { useRefreshRegistry } from '../../contexts/RefreshContext';
import { settingsService } from '../../services/settings-service';
import useIsMobile from '../../hooks/useIsMobile';
import useDrawerSwipe from '../../hooks/useDrawerSwipe';
import logger from '../../shared/utils/logger';
import NotificationBadge from '../ui/NotificationBadge';
import { EnableNotificationsBanner } from '../common';
import packageJson from '../../../package.json';

/**
 * MasterLayout component
 * 
 * Modern and minimal layout component that provides:
 * 1. Responsive sidebar navigation (mobile-first)
 * 2. Role-based menu items
 * 3. Content area for lazy-loaded child routes
 */
const MasterLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [rankingEnabled, setRankingEnabled] = useState<boolean>(false);
  const { user, logout } = useAuth();
  const { runRefresh } = useRefreshRegistry();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    settingsService.getSetting('enable_ranking_report')
      .then(setting => setRankingEnabled(setting.value === 'true'))
      .catch((err: unknown) => logger.error('Error loading ranking setting:', err));
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen]);

  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  // Swipe gestures: left-edge swipe opens the drawer, swipe-left closes it (mobile only).
  const isMobile = useIsMobile();
  const { panelHandlers } = useDrawerSwipe({
    enabled: isMobile,
    isOpen: isSidebarOpen,
    onOpen: openSidebar,
    onClose: closeSidebar,
  });

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const isActive = useCallback((path: string) => {
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Define menu item interface
  interface MenuItem {
    id: string;
    name: string;
    path: string;
    icon: string;
    roles: string[];
    order: number;
  }

  // Define navigation links based on user role
  const navLinks = useMemo(() => {
    const userRole = user?.isAdmin ? 'admin' : (user?.role || 'user');
    
    // All available menu items with role-based access and order
    const menuItems: MenuItem[] = [
      { 
        id: 'dashboard',
        name: 'Dashboard', 
        path: '/dashboard', 
        icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
        roles: ['user', 'admin'],
        order: 10
      },
      { 
        id: 'advanced-delivery',
        name: 'Advanced Delivery', 
        path: '/delivery/advanced', 
        icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
        roles: ['user', 'admin'],
        order: 25
      },
      {
        id: 'reports',
        name: 'Reports',
        path: '/reports/delivery',
        icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
        roles: ['user', 'admin'],
        order: 30
      },
      {
        id: 'ranking',
        name: 'Ranking',
        path: '/reports/ranking',
        icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
        roles: ['user', 'admin'],
        order: 31
      },
      {
        id: 'notifications',
        name: 'Notifications',
        path: '/notifications',
        icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
        roles: ['user', 'admin'],
        order: 40
      },
      { 
        id: 'customers',
        name: 'Customers', 
        path: '/customers', 
        icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
        roles: ['admin'],
        order: 50
      },
      { 
        id: 'orders',
        name: 'Orders', 
        path: '/orders', 
        icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
        roles: ['admin'],
        order: 60
      },
      { 
         id: 'employees',
        name: 'Employees', 
        path: '/employees', 
        icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
        roles: ['admin'],
        order: 70
      },
      { 
        id: 'settings',
        name: 'Settings', 
        path: '/settings', 
        icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
        roles: ['admin'],
        order: 80
      },
      {
        id: 'updates',
        name: 'Updates', 
        path: '/updates', 
        icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
        roles: ['admin'],
        order: 85
      },
      {
        id: 'admin-reports',
        name: 'Admin Reports',
        path: '/reports/delivery/admin',
        icon: 'M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z',
        roles: ['admin'],
        order: 90
      },
      {
        id: 'route-analysis',
        name: 'Route Analysis',
        path: '/reports/route-analysis',
        icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
        roles: ['admin'],
        order: 91
      },
      {
        id: 'profile',
        name: 'Profile',
        path: '/profile',
        icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
        roles: ['user', 'admin'],
        order: 100 // Place at the end
      },
    ];
    
    // Filter items by role, hide ranking if disabled, sort by order
    return menuItems
      .filter(item => item.roles.includes(userRole))
      .filter(item => item.id !== 'ranking' || rankingEnabled)
      .sort((a, b) => a.order - b.order);
  }, [user, rankingEnabled]);

  // Render navigation links
  const renderNavLinks = useCallback(() => {
    return navLinks.map((link) => (
      <Link
        key={link.id}
        to={link.path}
        className={`flex items-center px-4 py-3 mb-1 transition-colors duration-200 rounded-lg font-medium ${
          isActive(link.path)
            ? 'bg-primary-50 text-primary-700'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        onClick={() => {
          if (window.innerWidth < 768) {
            setIsSidebarOpen(false);
          }
        }}
      >
        <svg
          className={`w-5 h-5 ${
            isActive(link.path) ? 'text-primary-700' : 'text-gray-500'
          }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d={link.icon}
          />
        </svg>
        <span className="ml-3 text-sm font-medium">{link.name}</span>
      </Link>
    ));
  }, [navLinks, isActive, setIsSidebarOpen]);

  return (
    <div className="flex h-screen-safe bg-gray-50">
      {/* Mobile sidebar overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden ${
          isSidebarOpen ? '' : 'pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isSidebarOpen}
      >
        {/* Backdrop overlay */}
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity duration-300 ${
            isSidebarOpen ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden="true"
          onClick={toggleSidebar}
        ></div>

        {/* Mobile sidebar */}
        <div
          className={`relative flex flex-col w-full max-w-xs h-full pt-safe-t pb-4 bg-white shadow-xl transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          {...panelHandlers}
        >
          {/* Close button */}
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={toggleSidebar}
            >
              <span className="sr-only">Close sidebar</span>
              <svg
                className="w-6 h-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Logo */}
          <div className="flex items-center px-6 pt-8 pb-6">
            <img
              className="h-8 w-auto"
              src="/images/apple-pie-logo.png"
              alt="Apple Pie"
            />
            <span className="ml-3 text-lg font-semibold text-gray-900">Apple Pie</span>
          </div>
          
          <div className="px-2 mt-2 flex-1 overflow-y-auto">
            <nav className="space-y-1 px-3">
              {renderNavLinks()}
            </nav>
          </div>

          {/* User profile in mobile sidebar */}
          <div className="border-t border-gray-200 mt-6 pt-4 pb-safe-b px-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700">
                  {user?.username?.charAt(0) || 'U'}
                </div>
              </div>
              <div className="ml-3">                
                <button
                  onClick={handleLogout}
                  className="text-xs font-medium text-gray-500 hover:text-primary-600"
                >
                  Sign out
                </button>
              </div>
            </div>
            <div className="mt-3 text-center">
              <p className="text-xs text-gray-400">v{packageJson.version}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-white border-r border-gray-200 shadow-sm">
            <div className="flex items-center justify-between h-16 flex-shrink-0 px-4 border-b border-gray-200">
              <div className="flex items-center">
                <img
                  className="h-8 w-auto"
                  src="/images/apple-pie-logo.png"
                  alt="Apple Pie"
                />
                <span className="ml-3 text-lg font-semibold text-gray-900">Apple Pie</span>
              </div>
              <div className="flex items-center">
                <NotificationBadge isMobile={false} />
              </div>
            </div>
            <div className="flex-1 flex flex-col overflow-y-auto pt-5 pb-4">
              <nav className="flex-1 px-3 space-y-1">
                {renderNavLinks()}
              </nav>
            </div>
            {/* User profile in desktop sidebar */}
            <div className="flex-shrink-0 flex flex-col border-t border-gray-200 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700">
                    {user?.username?.charAt(0) || 'U'}
                  </div>
                </div>
                <div className="ml-3">                  
                  <button
                    onClick={handleLogout}
                    className="text-xs font-medium text-gray-500 hover:text-primary-600"
                  >
                    Sign out
                  </button>
                </div>
              </div>
              <div className="mt-3 text-center w-full">
                <p className="text-xs text-gray-400">v{packageJson.version}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Mobile header with menu button */}
        <div className="md:hidden flex items-center justify-between min-h-16 pt-safe-t bg-white border-b border-gray-200 shadow-sm px-4">
          <button
            type="button"
            className="text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={toggleSidebar}
          >
            <span className="sr-only">Open sidebar</span>
            <svg
              className="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h7"
              />
            </svg>
          </button>
          
          <div className="flex items-center">
            <NotificationBadge isMobile={true} />
          </div>
        </div>
        
        {/* Content area */}
        <main className="flex-1 relative overflow-y-auto overscroll-y-contain focus:outline-none">
          {/* Prompt users to enable web push so their FCM device token is registered */}
          <EnableNotificationsBanner />
          {/* Pull-to-refresh: reloads the current page's data (each page registers
              its fetch fn via usePageRefresh). isTreeScrollable in the library only
              fires the pull when <main> is scrolled to the top. */}
          {/* NOTE: do NOT pass isPullable={false} (e.g. to disable this on desktop).
              The library's own stylesheet sets `.ptr__children { overflow: hidden }`,
              and the only code that restores `overflow-y: auto` is its internal
              initContainer(), reachable solely from the onEnd/onRefresh handlers that
              isPullable gates. Turning it off leaves every page clipped and unscrollable. */}
          <PullToRefresh
            onRefresh={runRefresh}
            pullingContent={<div className="text-center text-gray-400 py-2 text-sm">Pull to refresh…</div>}
            refreshingContent={
              <div className="flex justify-center py-2">
                <span className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500" />
              </div>
            }
          >
            {/* Extra bottom padding, not plain py-6: the last element on a page used to
                sit flush against the bottom of the scroll container, leaving a native
                <select> at the end of a page no room to open its list (and making the
                final row awkward to tap on mobile). */}
            <div className="pt-6 pb-24">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                {/* This is where the Outlet component will render the child routes */}
                <Outlet />
              </div>
            </div>
          </PullToRefresh>
        </main>
      </div>
    </div>
  );
};

export default MasterLayout;

